import type { ThemeConfig } from 'antd';
import { colors, fontFamily } from './tokens';

/**
 * Theme antd (bản cài 5.29) — đặt token theo vai trò thay vì ghi đè CSS từng trang.
 * Mọi Button/Tag/Menu/Table... tự đổi sang bộ màu app mà không phải sửa trang nào.
 */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: colors.accent,
    colorLink: colors.accent,
    colorLinkHover: colors.accentDeep,
    colorSuccess: colors.successInk,
    // Nền/viền trạng thái đặt tường minh: để antd tự sinh từ màu chữ đậm thì ra nền xám đục,
    // Tag "Đang chạy"/"Đã duyệt" chỉ đạt ~2.7:1.
    colorSuccessBg: colors.successSoft,
    colorSuccessBorder: '#A8DDBF',
    colorWarning: '#D97706',
    colorWarningBg: colors.warningSoft,
    colorWarningBorder: '#F5C99B',
    colorError: colors.dangerInk,
    colorErrorBg: colors.dangerSoft,
    colorErrorBorder: '#F3B4AE',
    colorInfo: colors.accent,
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    // Chữ phụ mang thông tin (breadcrumb, tiêu đề Statistic, Typography type="secondary", mô tả
    // Empty/Form, placeholder ô lọc "Tất cả vai trò"...) đều đọc 3 token này. textMuted #9A8F9E
    // chỉ 3.1:1 trên nền trắng → dùng textSecondary (5.8:1 trắng, 5.2:1 trên surfaceMuted).
    // colorTextQuaternary (chữ bị vô hiệu hoá) giữ mặc định antd.
    colorTextTertiary: colors.textSecondary,
    colorTextDescription: colors.textSecondary,
    colorTextPlaceholder: colors.textSecondary,
    colorBorder: '#E2D6DD',
    colorBorderSecondary: colors.line,
    colorBgLayout: colors.bg,
    colorBgContainer: colors.surface,
    colorFillAlter: colors.surfaceMuted,
    fontFamily,
    fontSize: 14,
    borderRadius: 10,
    borderRadiusLG: 14,
    borderRadiusSM: 6,
    controlHeight: 36,
    boxShadowTertiary: '0 1px 2px rgba(120, 60, 100, 0.06)',
    // Tông tím như app (purpleInk/purpleSoft 28/09): <Tag color="purple"> = chữ #5B3FD1 trên
    // nền #EFEBFF. Tag đọc purple7 (chữ) / purple1 (nền) / purple3 (viền, sinh từ `purple`).
    purple: colors.purpleInk,
    purple1: colors.purpleSoft,
    purple7: colors.purpleInk,
  },
  components: {
    Layout: {
      bodyBg: colors.bg,
      siderBg: colors.surface,
      headerBg: colors.surface,
    },
    // Menu sáng (Dropdown, menu trong trang). Thanh bên dùng sidebarMenuTheme bên dưới.
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: colors.textSecondary,
      itemHoverColor: colors.textPrimary,
      itemHoverBg: colors.surfaceMuted,
      itemSelectedBg: colors.accentSoft,
      itemSelectedColor: colors.accent,
      groupTitleColor: colors.textSecondary,
      groupTitleFontSize: 11,
      itemBorderRadius: 10,
      itemHeight: 38,
      itemMarginInline: 8,
      iconSize: 16,
      activeBarBorderWidth: 0,
    },
    Table: {
      headerBg: colors.surfaceMuted,
      headerColor: colors.textSecondary,
      headerSplitColor: 'transparent',
      rowHoverBg: '#FDF6FA',
      borderColor: colors.line,
      // Chữ bảng trống dạng chuỗi (locale.emptyText, vd "Chưa có dữ liệu.") và "Not Found" trong ô
      // lọc được antd vẽ bằng colorTextDisabled (#BFBFBF, 1.8:1). Chỉ đè trong Table — chữ vô hiệu
      // hoá ở chỗ khác giữ mặc định.
      colorTextDisabled: colors.textSecondary,
    },
    // Tag màu có sẵn (green/gold/orange...) lấy chữ từ màu bậc 7 của antd: green 3.4:1, gold 2.8:1,
    // orange 3.3:1, cyan 3.4:1, lime 2.5:1, volcano 4.4:1 trên nền bậc 1. Chỉ đè trong Tag (Button,
    // Badge... giữ nguyên). red/magenta/blue/geekblue bậc 7 đã ≥4.9:1; purple đặt ở token chung.
    Tag: {
      green7: colors.successInk, // 5.3:1
      gold7: colors.starInk, // 5.7:1
      orange7: colors.warningInk, // 5.9:1
      cyan7: '#006D75', // cyan-8 antd, 5.8:1
      lime7: '#3F6600', // lime-9 antd, 6.6:1
      volcano7: '#AD2102', // volcano-8 antd, 6.4:1
    },
    // Tiêu đề + số lớn: xanh dương đậm `heading` như app. Chỉ đặt theo từng component (không đổi
    // colorTextHeading toàn cục — nhãn Form / đầu bảng cũng đọc token đó).
    Typography: {
      colorTextHeading: colors.heading,
    },
    Card: {
      bodyPadding: 20,
      colorTextHeading: colors.heading,
    },
    Tabs: {
      itemSelectedColor: colors.accent,
      inkBarColor: colors.accent,
    },
    Modal: {
      titleFontSize: 18,
      titleColor: colors.heading,
    },
    Button: {
      fontWeight: 600,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Statistic: {
      contentFontSize: 26,
      colorTextHeading: colors.heading,
    },
    // Tiêu đề trang kết quả (403 "Chức năng này chỉ dành cho Admin", 404...).
    Result: {
      colorTextHeading: colors.heading,
    },
  },
};

/**
 * Chỉ áp cho các trang xác thực (AuthLayout bọc ConfigProvider riêng; ConfigProvider lồng nhau
 * thừa kế antdTheme nên không đụng trang khác).
 * - controlHeightLG: antd mặc định = controlHeight × 1.25 = 45px → ép 48 cho khớp lưới 8.
 *   Ô `size="large"` dùng fontSizeLG 16px nên iOS không tự phóng to khi gõ.
 */
export const authTheme: ThemeConfig = {
  token: {
    controlHeightLG: 48,
  },
  components: {
    Form: {
      itemMarginBottom: 20,
    },
  },
};

/**
 * Chỉ áp cho Menu ở thanh bên (bọc bằng ConfigProvider riêng để không lan sang Dropdown).
 * Nền Đêm + quầng sáng vẽ ở KHUNG thanh bên (layouts/styles.module.scss) → mục menu trong suốt.
 * Menu bật ra khi thu gọn nằm ngoài khung nên dùng nền navy đặc.
 */
export const sidebarMenuTheme: ThemeConfig = {
  components: {
    Menu: {
      darkItemBg: 'transparent',
      darkSubMenuItemBg: colors.sidebarBgDeep,
      darkPopupBg: colors.sidebarBg,
      darkItemColor: colors.sidebarText,
      darkItemHoverColor: '#FFFFFF',
      darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
      darkItemSelectedBg: colors.accent,
      darkItemSelectedColor: '#FFFFFF',
      darkGroupTitleColor: colors.onNight,
      itemBorderRadius: 10,
      itemHeight: 40,
      itemMarginInline: 10,
      iconSize: 16,
      collapsedIconSize: 17,
      // = bề rộng thanh bên thu gọn (.sidebarCollapsed 72px). Mặc định antd 90px (controlHeightLG×2)
      // → mục rộng hơn khung: viên hồng mục đang chọn bị cắt vuông mép phải, icon lệch phải ~9px.
      collapsedWidth: 72,
      subMenuItemBorderRadius: 10,
    },
  },
};
